/**
 * Temporary replacement for url_helper until the new URL functions are fully implemented and tested across the app. This file will be removed once the transition is complete.
 * @todo: Use some proper state management solution (like Zustand) to handle URL state across the app instead of relying on these functions.
 */

export const URL_SEARCH_PARAMS = new URLSearchParams(window.location.search);

const PARSE_FLOAT_KEYS: ReadonlyArray<string> = [
  "road_width",
  "q0",
  "width_of_central_reservation",
  "border_area_left_width",
  "border_area_right_width",
  "border_area_left_kerb_distance",
  "border_area_right_kerb_distance",
  "maintenance_factor",
  "height_of_the_light_point",
  "height_of_the_light_point_min",
  "height_of_the_light_point_max",
  "height_of_the_light_point_step",
  "overhang",
  "overhang_min",
  "overhang_max",
  "overhang_step",
  "tilt_of_the_luminaire",
  "tilt_of_the_luminaire_min",
  "tilt_of_the_luminaire_max",
  "tilt_of_the_luminaire_step",
];

const PARSE_INT_KEYS: ReadonlyArray<string> = [
  "number_of_lanes",
  "distance_between_poles",
  "distance_between_poles_min",
  "distance_between_poles_max",
  "distance_between_poles_step",
];

const PARSE_BOOL_KEYS: ReadonlyArray<string> = [
  "border_area_left",
  "border_area_right",
  "ignore_rei",
  "ignore_ti",
  "should_optimize_distance_between_poles",
  "should_optimize_height_of_the_light_point",
  "should_optimize_overhang",
  "should_optimize_tilt_of_the_luminaire",
  "htr_url_statefulurl",
  "prepare_pdf",
];

const PARSE_RANGE_KEYS: ReadonlyArray<string> = [
  "distance_between_poles_min",
  "distance_between_poles",
  "distance_between_poles_max",
  "height_of_the_light_point",
  "height_of_the_light_point",
  "height_of_the_light_point_min",
  "height_of_the_light_point_max",
  "overhang",
  "tilt_of_the_luminaire",
];

const URL_PARAM_KEYS: ReadonlyArray<string> = [
  "htr_url_statefulurl",
  "portfolio_id",
  "fsc_api_url",
  "family_ids",
  "family_id",
  "carriage_way",
  "border_area_left",
  "border_area_right",
  "road_width",
  "width_of_central_reservation",
  "number_of_lanes",
  "covering",
  "q0",
  "border_area_left_width",
  "border_area_right_width",
  "border_area_left_kerb_distance",
  "border_area_right_kerb_distance",
  "road_classes",
  "border_area_right_classes",
  "border_area_left_classes",
  "luminaire_config",
  "optimization_type",
  "ignore_ti",
  "ignore_rei",
  "distance_between_poles_min",
  "distance_between_poles_max",
  "distance_between_poles_step",
  "should_optimize_distance_between_poles",
  "distance_between_poles",
  "should_optimize_height_of_the_light_point",
  "height_of_the_light_point_min",
  "height_of_the_light_point_max",
  "height_of_the_light_point_step",
  "height_of_the_light_point",
  "should_optimize_overhang",
  "overhang_min",
  "overhang_max",
  "overhang_step",
  "overhang",
  "should_optimize_tilt_of_the_luminaire",
  "tilt_of_the_luminaire_min",
  "tilt_of_the_luminaire_max",
  "tilt_of_the_luminaire_step",
  "tilt_of_the_luminaire",
  "maintenance_factor",
  "selected_result",
  "project_id",
  "name_of_road",
  "project_name",
  "location",
  "comment",
  "lang_code",
  "prepare_pdf",
  "search_value",
  "color",
  "flux",
  "optic",
  "pdf_header_margin_top",
  "pdf_header_margin_bottom",
  "pdf_header_margin_left",
  "pdf_header_margin_right",
] as const;

export const AppURLParams = Object.fromEntries(URL_PARAM_KEYS.map((key) => [key, URL_SEARCH_PARAMS.get(key)])) as Record<
  (typeof URL_PARAM_KEYS)[number],
  string | null
>;

export function getTypedVal(key: string, defaultValue?: string | bigint | number | boolean): any {
  let value: string | number | boolean | null = AppURLParams[key];
  const canParse = (value || typeof value == "boolean") && value !== null;

  if (canParse) {
    if (PARSE_FLOAT_KEYS.includes(key)) value = parseFloat(value as string);
    if (PARSE_INT_KEYS.includes(key)) value = parseInt(value as string);
    if (PARSE_BOOL_KEYS.includes(key)) value = JSON.parse(value as string);
  }

  return value === null ? defaultValue : value;
}

export function getURL() {
  const urlParams = new URLSearchParams(window.location.search);
  if (!getTypedVal("htr_url_statefulurl")) return urlParams.toString();

  Object.entries(AppURLParams).forEach(([key, value]) => {
    urlParams.set(key, value ?? "");
  });

  return urlParams.toString();
}

export function resetURL() {
  Object.keys(AppURLParams).forEach((key) => ((AppURLParams as any)[key] = null));
  const urlParams = new URLSearchParams(window.location.search);
  window.history.replaceState(null, "", `${window.location.origin}?portfolio_id=${urlParams.get("portfolio_id")}`);
}

export function setURL(
  key: string,
  value:
    | string
    | bigint
    | number
    | boolean
    | {
        value?: number | string;
        rangeValue?: {
          min: number;
          max: number;
          step: number;
        };
      }
): void {
  if (PARSE_RANGE_KEYS.indexOf(key) < 0) {
    // @ts-ignore
    AppURLParams[key] = value;
    return;
  }

  // @ts-ignore
  AppURLParams[key] = value.value;
  ["min", "max", "step"].forEach((suffix) => {
    // @ts-ignore
    AppURLParams[`${key}_${suffix}`] = value.rangeValue[suffix];
  });
}

export function setStateURL(key?: string, value?: string | bigint | number | boolean): void {
  if (!getTypedVal("htr_url_statefulurl")) return;
  if (key && (value || value === "" || value === 0)) setURL(key, value);

  const urlParams = new URLSearchParams(window.location.search);
  Object.entries(AppURLParams).forEach(([paramKey, paramValue]) => {
    if (["boolean", "bigint", "number", "string"].includes(typeof paramValue)) {
      urlParams.set(paramKey, String(paramValue));
    }
  });

  window.history.replaceState(null, "", `${window.location.origin}?${urlParams.toString()}`);
}

export function setObjectURL(obj: any): void {
  if (!getTypedVal("htr_url_statefulurl")) return;
  Object.entries(obj).forEach(([key, value]) => {
    setURL(
      key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
      value as any
    );
  });
  setStateURL();
}

export function setFromURLObject(obj: any, callback?: any): any {
  if (!getTypedVal("htr_url_statefulurl")) return obj;
  const hasCallback = typeof callback === "function";

  Object.entries(obj).forEach(([key]) => {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    const tmpVal = getTypedVal(snakeKey);
    const hasValue = (typeof tmpVal !== "undefined" || typeof tmpVal == "boolean") && tmpVal !== null;

    // If URL param doesn't exist, skip - don't write defaults back to URL
    if (!hasValue) {
      return;
    }

    if (PARSE_RANGE_KEYS.includes(snakeKey)) {
      obj[key].value = tmpVal;
      ["min", "max", "step"].forEach((suffix) => {
        obj[key].rangeValue[suffix] = getTypedVal(`${snakeKey}_${suffix}`);
      });
    } else {
      obj[key] = tmpVal;
    }

    if (hasCallback) callback(obj);
  });

  return obj;
}
